import type { CitationView } from "@/lib/ai/types";
import type { AnswerInline } from "@/lib/ai/answer-inline";
import type {
  DocumentListCollectionFilter,
  DocumentListDepartmentFilter,
} from "@/lib/ui/document-list";
import {
  askDocumentHref,
  askDocumentLabel,
} from "@/lib/ui/document-view";

export const AGENT_RUN_LIMIT = 50;
export const DOCUMENT_RUN_LIMIT = 50;
export const DOCUMENT_BRIEFING_AGENT_ID = "document-briefing";
export const DOCUMENT_COMPARE_AGENT_ID = "document-compare";
export const DOCUMENT_REVISE_AGENT_ID = "document-revise";

export const agentsCopy = {
  nav: "Агенты",
  title: "Агенты",
  description: "Задачи по документам: разбор, сравнение и правки по норме, не диалог.",
  briefingName: "Разбор документа",
  briefingDescription:
    "Сводка, сроки, обязательства и риски по выбранному файлу.",
  compareName: "Сравнение документов",
  compareDescription:
    "Что общего и где расходятся два файла или две версии одного файла.",
  reviseName: "Доработка документа",
  reviseDescription:
    "Готовые замены в черновике по норме и Wiki. Только формулировки из источников.",
  documentLabel: "Документ",
  documentALabel: "Первый документ",
  documentBLabel: "Второй документ",
  reviseDraftLabel: "Черновик",
  reviseNormLabel: "Норма",
  documentPlaceholder: "Название",
  focusLabel: "На чём заострить внимание?",
  focusHelp: "Необязательно. Например: сроки поставки.",
  run: "Разобрать",
  pending: "Готовим разбор…",
  compareRun: "Сравнить",
  comparePending: "Готовим сравнение…",
  reviseRun: "Доработать",
  revisePending: "Готовим доработку…",
  emptyTitle: "Нет доступных документов",
  emptyDescription: "Загрузите файл, чтобы разобрать его.",
  emptyAction: { href: "/documents", label: "К документам" },
  lockedFirstHelp: "Этот файл — первый. Выберите второй.",
  reviseLockedHelp: "Этот файл — черновик. Выберите норму.",
  compareNeedOther: "Чтобы сравнить, нужен ещё один доступный файл.",
  compareNeedVersion:
    "Чтобы сравнить этот файл с собой, загрузите ещё одну версию.",
  reviseNeedOther: "Чтобы доработать, нужен ещё один доступный файл — норма.",
  versionLabel: "Версия",
  missingDocument: "Выберите документ",
  missingOther: "Выберите второй документ",
  missingNorm: "Выберите норму",
  sameDocument: "Выберите два разных файла",
  sameVersion: "Выберите две разные версии",
  denied: "Нужно войти",
  forbidden: "Нет доступа к этому документу",
  errorTitle: "Разбор",
  compareErrorTitle: "Сравнение",
  reviseErrorTitle: "Доработка",
  sources: "Фрагменты",
  fragmentFallback: "Фрагмент",
  catalogTab: "Каталог",
  analysisTab: "Анализ",
  history: "Недавний анализ",
  searchLabel: "Поиск",
  searchPlaceholder: "Название или агент",
  emptyRunsTitle: "Пока нет анализов",
  emptySearchTitle: "Ничего не найдено",
  back: "К агентам",
  repeat: "Повторить",
  focusValue: "Фокус",
  files: "Файлы",
  filesHide: "Скрыть файлы",
  pickerHint: "Выберите файл",
  pickerHintA: "Сейчас: первый документ",
  pickerHintB: "Сейчас: второй документ",
  revisePickerHintA: "Сейчас: черновик",
  revisePickerHintB: "Сейчас: норма",
  reviseCurrent: "Сейчас",
  reviseProposed: "Заменить на",
  reviseInsert: "Вставить",
  reviseWikiEdited: "Wiki, правка человека",
  reviseOpenSection: "К разделу",
  reviseSourcesEmpty: "Нет фрагментов",
  reviseAccept: "Принять",
  reviseReject: "Отклонить",
  reviseSave: "Сохранить как новую версию",
  reviseSaving: "Сохраняем…",
  reviseSaveHelp:
    "Принятые правки попадут в новую версию. DOCX — track changes в Word; PDF — лист правок и итоговый текст.",
  revisePreviewTitle: "Итоговый текст",
  revisePreviewMissing: "Не удалось собрать превью для выбранных правок.",
  revisePreviewOverlap: "Правки пересекаются — превью недоступно.",
  reviseNeedWrite: "Чтобы сохранить версию, нужно право на запись черновика.",
  reviseNeedText: "Текст черновика ещё не готов.",
  reviseNeedAccepted: "Примите хотя бы одну правку.",
  reviseMissingText: "Не удалось найти заменяемый текст в черновике.",
  reviseOverlap: "Правки пересекаются. Отклоните одну из них.",
  reviseForbiddenWrite: "Нет прав на новую версию",
  reviseNoDraft: "Черновик не найден",
  pickerEmpty: "Нет файлов",
  delete: "Удалить",
  deleteTitle: "Удалить анализ?",
  deleteConfirm: "Удалить",
  deleteCancel: "Отмена",
} as const;

export function agentRunDeleteMessage(title: string): string {
  return `«${title}» будет удалён без восстановления.`;
}

export type AgentRunListItem = {
  id: string;
  agentId: string;
  title: string;
  createdAt: Date;
};

export type AgentRunView = {
  id: string;
  agentId: string;
  title: string;
  focus: string;
  answer: string;
  citations: CitationView[];
  documentId: string | null;
  status: "answered" | "no_sources";
  createdAt: Date;
};

export function splitReviseCitations(
  citations: CitationView[],
  draftDocumentId: string | null,
): { draft: CitationView[]; norm: CitationView[] } {
  if (!draftDocumentId) {
    return { draft: citations, norm: [] };
  }
  return {
    draft: citations.filter((item) => item.documentId === draftDocumentId),
    norm: citations.filter((item) => item.documentId !== draftDocumentId),
  };
}

export function reportCitationGroupKey(
  item: Pick<CitationView, "documentId" | "version">,
): string {
  return `${item.documentId}:${item.version}`;
}

export function reportCitationGroupLabel(
  item: Pick<CitationView, "documentTitle" | "version">,
): string {
  const title = item.documentTitle.trim() || "Документ";
  if (title.includes(`версия ${item.version}`)) {
    return title;
  }
  return `${title} · версия ${item.version}`;
}

export function reportCitationRowLabel(
  item: Pick<CitationView, "heading" | "sectionPath" | "documentTitle">,
): string {
  const title = item.documentTitle.trim();
  const heading = item.heading?.trim() ?? "";
  if (heading && heading !== title) {
    return heading;
  }
  const section = item.sectionPath?.trim() ?? "";
  if (section && section !== title) {
    const leaf = section.split("/").at(-1)?.trim() ?? "";
    return leaf || section;
  }
  return agentsCopy.fragmentFallback;
}

export function groupReportCitations(
  citations: CitationView[],
): Array<{ key: string; label: string; items: CitationView[] }> {
  const groups: Array<{ key: string; label: string; items: CitationView[] }> =
    [];
  const indexByKey = new Map<string, number>();
  for (const item of citations) {
    const key = reportCitationGroupKey(item);
    const at = indexByKey.get(key);
    if (at == null) {
      indexByKey.set(key, groups.length);
      groups.push({
        key,
        label: reportCitationGroupLabel(item),
        items: [item],
      });
      continue;
    }
    groups[at]!.items.push(item);
  }
  return groups;
}

export type ReportCiteTone = "a" | "b";

export type ReportInline =
  | { type: "text"; value: string }
  | { type: "code"; value: string }
  | { type: "cite"; index: number }
  | { type: "cite-pair"; left: number; right: number }
  | { type: "strong"; children: ReportInline[] }
  | { type: "em"; children: ReportInline[] };

export function reportCiteTone(
  item: Pick<CitationView, "documentId" | "version">,
  files: string[],
): ReportCiteTone {
  const key = reportCitationGroupKey(item);
  return files.indexOf(key) > 0 ? "b" : "a";
}

export function reportCiteFiles(citations: CitationView[]): string[] {
  const files: string[] = [];
  for (const item of citations) {
    const key = reportCitationGroupKey(item);
    if (!files.includes(key)) {
      files.push(key);
    }
  }
  return files;
}

export function sortReportCitationsByFile(
  items: CitationView[],
  files: string[],
): CitationView[] {
  return [...items].sort((left, right) => {
    const a = files.indexOf(reportCitationGroupKey(left));
    const b = files.indexOf(reportCitationGroupKey(right));
    return (a < 0 ? files.length : a) - (b < 0 ? files.length : b);
  });
}

export function reportCiteClass(
  tone: ReportCiteTone,
  selected = false,
): string {
  return `chat-cite chat-cite--file-${tone}${selected ? " is-on" : ""}`;
}

export function reportCitationPartners(
  citations: CitationView[],
): Map<number, number> {
  const byLabel = new Map<string, CitationView[]>();
  for (const item of citations) {
    const label = reportCitationRowLabel(item).toLowerCase();
    const list = byLabel.get(label) ?? [];
    list.push(item);
    byLabel.set(label, list);
  }
  const partners = new Map<number, number>();
  for (const group of byLabel.values()) {
    if (group.length !== 2) {
      continue;
    }
    const [left, right] = group;
    if (
      !left ||
      !right ||
      reportCitationGroupKey(left) === reportCitationGroupKey(right)
    ) {
      continue;
    }
    partners.set(left.index, right.index);
    partners.set(right.index, left.index);
  }
  return partners;
}

export function mergePairedCiteNodes(
  nodes: AnswerInline[],
  partners: Map<number, number>,
): ReportInline[] {
  const mapped: ReportInline[] = nodes.map((node) => {
    if (node.type === "strong" || node.type === "em") {
      return {
        type: node.type,
        children: mergePairedCiteNodes(node.children, partners),
      };
    }
    return node;
  });
  const out: ReportInline[] = [];
  for (let i = 0; i < mapped.length; i += 1) {
    const node = mapped[i]!;
    if (node.type !== "cite") {
      out.push(node);
      continue;
    }
    const partner = partners.get(node.index);
    if (partner == null) {
      out.push(node);
      continue;
    }
    let nextAt = i + 1;
    const between = mapped[nextAt];
    if (between?.type === "text" && between.value.trim() === "") {
      nextAt += 1;
    }
    const next = mapped[nextAt];
    if (next?.type === "cite" && next.index === partner) {
      out.push({ type: "cite-pair", left: node.index, right: next.index });
      i = nextAt;
      continue;
    }
    out.push(node);
  }
  return out;
}

export function agentsHref(): string {
  return "/agents";
}

export function agentsTrail(agentId?: string): {
  label: string;
  href?: string;
}[] {
  if (!agentId) {
    return [{ label: agentsCopy.nav }];
  }
  return [
    { label: agentsCopy.nav, href: agentsHref() },
    { label: agentRunKindLabel(agentId) },
  ];
}

export function agentsAnalysisHref(agentId?: string): string {
  const params = new URLSearchParams();
  params.set("tab", "analysis");
  if (agentId) {
    params.set("agent", agentId);
  }
  return `/agents?${params.toString()}`;
}

export function catalogAgentHref(agentId: string): string {
  return `/agents?agent=${encodeURIComponent(agentId)}`;
}

export function briefDocumentHref(documentId: string): string {
  return `/agents?agent=${encodeURIComponent(DOCUMENT_BRIEFING_AGENT_ID)}&documentId=${encodeURIComponent(documentId)}`;
}

export function compareDocumentHref(documentId: string): string {
  return `/agents?agent=${encodeURIComponent(DOCUMENT_COMPARE_AGENT_ID)}&documentId=${encodeURIComponent(documentId)}`;
}

export function reviseDocumentHref(documentId: string): string {
  return `/agents?agent=${encodeURIComponent(DOCUMENT_REVISE_AGENT_ID)}&documentId=${encodeURIComponent(documentId)}`;
}

export function parseCatalogAgentId(raw: string | undefined): string | undefined {
  if (
    raw === DOCUMENT_BRIEFING_AGENT_ID ||
    raw === DOCUMENT_COMPARE_AGENT_ID ||
    raw === DOCUMENT_REVISE_AGENT_ID
  ) {
    return raw;
  }
  return undefined;
}

export function pickCatalogAgentId(params: {
  agent?: string;
  compare?: string;
  documentId?: string;
}): string | undefined {
  const agent = parseCatalogAgentId(params.agent);
  if (agent) {
    return agent;
  }
  if (params.compare === "1") {
    return DOCUMENT_COMPARE_AGENT_ID;
  }
  if (params.documentId?.trim()) {
    return DOCUMENT_BRIEFING_AGENT_ID;
  }
  return undefined;
}

export function agentsSelectedTabIndex(tab?: string): number {
  return tab === "analysis" ? 1 : 0;
}

export function wantsCompareForm(raw: string | undefined): boolean {
  return raw === "1";
}

export function pickBriefingDocumentId(
  documents: Array<{ id: string }>,
  raw: string | undefined,
): string | undefined {
  const id = raw?.trim() ?? "";
  if (!id) {
    return undefined;
  }
  return documents.some((item) => item.id === id) ? id : undefined;
}

export function pickCatalogVersion(raw: string | undefined): number | undefined {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export function versionIdByNumber(
  versions: AgentDocumentVersion[],
  version: number | undefined,
): string {
  if (version == null) {
    return "";
  }
  return versions.find((item) => item.version === version)?.id ?? "";
}

export type AgentRunSide = {
  documentId: string;
  title: string;
  version: number;
};

export function agentRunSides(
  run: Pick<AgentRunView, "documentId" | "citations">,
): AgentRunSide[] {
  const files = reportCiteFiles(run.citations);
  const sides: AgentRunSide[] = [];
  for (const key of files) {
    const item = run.citations.find(
      (citation) =>
        reportCitationGroupKey(citation) === key &&
        citation.accessible !== false,
    );
    if (!item) {
      continue;
    }
    sides.push({
      documentId: item.documentId,
      title: item.documentTitle,
      version: item.version,
    });
  }
  if (sides.length === 0 && run.documentId) {
    sides.push({ documentId: run.documentId, title: "", version: 0 });
  }
  return sides;
}

export function agentRunDocumentLinks(
  run: Pick<AgentRunView, "documentId" | "citations">,
): Array<{ id: string; title: string }> {
  const seen = new Set<string>();
  const links: Array<{ id: string; title: string }> = [];
  for (const side of agentRunSides(run)) {
    if (seen.has(side.documentId) || !side.documentId) {
      continue;
    }
    seen.add(side.documentId);
    links.push({
      id: side.documentId,
      title: side.title.trim() || agentsCopy.documentLabel,
    });
  }
  return links;
}

export function repeatAgentHref(params: {
  agentId: string;
  documentId?: string | null;
  otherDocumentId?: string | null;
  version?: number | null;
  otherVersion?: number | null;
  focus?: string;
}): string {
  const query = new URLSearchParams();
  query.set("agent", params.agentId);
  if (params.documentId) {
    query.set("documentId", params.documentId);
  }
  if (params.otherDocumentId) {
    query.set("otherDocumentId", params.otherDocumentId);
  }
  if (params.version && params.version > 0) {
    query.set("version", String(params.version));
  }
  if (params.otherVersion && params.otherVersion > 0) {
    query.set("otherVersion", String(params.otherVersion));
  }
  const focus = params.focus?.trim() ?? "";
  if (focus) {
    query.set("focus", focus);
  }
  return `/agents?${query.toString()}`;
}

export function repeatAgentRunHref(
  run: Pick<AgentRunView, "agentId" | "documentId" | "citations" | "focus">,
): string | null {
  const sides = agentRunSides(run);
  const first = sides[0];
  const documentId = first?.documentId ?? run.documentId;
  if (!documentId) {
    return null;
  }
  const second = sides[1];
  return repeatAgentHref({
    agentId: run.agentId,
    documentId,
    otherDocumentId: second?.documentId,
    version: first?.version,
    otherVersion: second?.version,
    focus: run.focus,
  });
}

export function nextAgentRunAction(
  run: Pick<AgentRunView, "agentId" | "documentId" | "citations" | "focus">,
): { href: string; label: string } | null {
  if (run.agentId === DOCUMENT_COMPARE_AGENT_ID) {
    const sides = agentRunSides(run);
    const first = sides[0];
    const documentId = first?.documentId ?? run.documentId;
    const second = sides[1];
    if (!documentId || !second?.documentId) {
      return null;
    }
    return {
      href: repeatAgentHref({
        agentId: DOCUMENT_REVISE_AGENT_ID,
        documentId,
        otherDocumentId: second.documentId,
        version: first?.version,
        otherVersion: second.version,
        focus: run.focus,
      }),
      label: agentsCopy.reviseRun,
    };
  }
  if (run.agentId === DOCUMENT_BRIEFING_AGENT_ID) {
    const documentId =
      agentRunDocumentLinks(run)[0]?.id ?? run.documentId ?? "";
    if (!documentId) {
      return null;
    }
    return {
      href: askDocumentHref(documentId),
      label: askDocumentLabel,
    };
  }
  return null;
}

export function agentRunHref(id: string): string {
  return `/agents/${id}`;
}

export function agentRunTitle(documentTitle: string, focus: string): string {
  const title = documentTitle.trim() || "Документ";
  const trimmed = focus.trim();
  return trimmed ? `${title} · ${trimmed}` : title;
}

export function compareRunTitle(
  titleA: string,
  titleB: string,
  focus: string,
): string {
  const pair = `${titleA.trim() || "Документ"} ↔ ${titleB.trim() || "Документ"}`;
  const trimmed = focus.trim();
  return trimmed ? `${pair} · ${trimmed}` : pair;
}

export function compareVersionLabel(version: number, created?: string): string {
  const label = `Версия ${version}`;
  return created ? `${label} · ${created}` : label;
}

export function latestCompareVersionId(
  versions: AgentDocumentVersion[],
): string {
  return [...versions].sort((left, right) => right.version - left.version)[0]
    ?.id ?? "";
}

export function otherCompareVersionId(
  versions: AgentDocumentVersion[],
  excludeId: string,
): string {
  return (
    [...versions]
      .sort((left, right) => right.version - left.version)
      .find((item) => item.id !== excludeId)?.id ?? ""
  );
}

export function compareVersionChoices(
  versions: AgentDocumentVersion[],
  excludeId?: string,
): AgentDocumentVersion[] {
  const sorted = [...versions].sort(
    (left, right) => right.version - left.version,
  );
  if (!excludeId) {
    return sorted;
  }
  return sorted.filter((item) => item.id !== excludeId);
}

export function prefillCompareOtherId(
  documentId: string,
  versions: AgentDocumentVersion[],
): string {
  return documentId && versions.length > 1 ? documentId : "";
}

export function canCompareRun(params: {
  documentId: string;
  otherDocumentId: string;
  versionId: string;
  otherVersionId: string;
}): boolean {
  if (!params.documentId || !params.otherDocumentId) {
    return false;
  }
  if (params.documentId === params.otherDocumentId) {
    return Boolean(
      params.versionId &&
        params.otherVersionId &&
        params.versionId !== params.otherVersionId,
    );
  }
  return true;
}

export function reviseRunTitle(
  draftTitle: string,
  normTitle: string,
  focus: string,
): string {
  const pair = `${draftTitle.trim() || "Документ"} ← ${normTitle.trim() || "Норма"}`;
  const trimmed = focus.trim();
  return trimmed ? `${pair} · ${trimmed}` : pair;
}

export function agentCatalogGlyph(
  agentId: string,
): "compare" | "revise" | "briefing" {
  if (agentId === DOCUMENT_COMPARE_AGENT_ID) {
    return "compare";
  }
  if (agentId === DOCUMENT_REVISE_AGENT_ID) {
    return "revise";
  }
  return "briefing";
}

export function agentRunKindLabel(agentId: string): string {
  if (agentId === DOCUMENT_COMPARE_AGENT_ID) {
    return agentsCopy.compareName;
  }
  if (agentId === DOCUMENT_REVISE_AGENT_ID) {
    return agentsCopy.reviseName;
  }
  return agentsCopy.briefingName;
}

export function agentRunListMeta(agentId: string, when: string): string {
  return `${agentRunKindLabel(agentId)} · ${when}`;
}

export function agentAnalysisCountLabel(count: number): string {
  if (count <= 0) {
    return "Нет анализов";
  }
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return `${count} анализов`;
  }
  if (n1 === 1) {
    return `${count} анализ`;
  }
  if (n1 >= 2 && n1 <= 4) {
    return `${count} анализа`;
  }
  return `${count} анализов`;
}

export type AgentDocumentVersion = {
  id: string;
  version: number;
  created?: string;
};

export type AgentDocumentChoice = {
  id: string;
  title: string;
  departmentId: string | null;
  departmentName: string | null;
  collectionId: string | null;
  collectionName: string | null;
  owner: string;
  versions: AgentDocumentVersion[];
};

export function filterAgentDocuments(
  rows: AgentDocumentChoice[],
  query: string,
  department: DocumentListDepartmentFilter = "all",
  collection: DocumentListCollectionFilter = "all",
): AgentDocumentChoice[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (department === "none" && row.departmentId) {
      return false;
    }
    if (
      department !== "all" &&
      department !== "none" &&
      row.departmentId !== department
    ) {
      return false;
    }
    if (collection === "none" && row.collectionId) {
      return false;
    }
    if (
      collection !== "all" &&
      collection !== "none" &&
      row.collectionId !== collection
    ) {
      return false;
    }
    if (!needle) {
      return true;
    }
    return [
      row.title,
      row.departmentName ?? "",
      row.collectionName ?? "",
      row.owner,
    ].some((value) => value.toLowerCase().includes(needle));
  });
}

export function filterAgentRuns(
  runs: AgentRunListItem[],
  query: string,
): AgentRunListItem[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return runs;
  }
  return runs.filter((run) => {
    const kind = agentRunKindLabel(run.agentId).toLowerCase();
    return run.title.toLowerCase().includes(needle) || kind.includes(needle);
  });
}

export const AGENT_CATALOG = [
  {
    id: DOCUMENT_BRIEFING_AGENT_ID,
    name: agentsCopy.briefingName,
    description: agentsCopy.briefingDescription,
  },
  {
    id: DOCUMENT_COMPARE_AGENT_ID,
    name: agentsCopy.compareName,
    description: agentsCopy.compareDescription,
  },
  {
    id: DOCUMENT_REVISE_AGENT_ID,
    name: agentsCopy.reviseName,
    description: agentsCopy.reviseDescription,
  },
] as const;

export type BriefingState = {
  error: string | null;
  documentId: string;
  documentTitle: string | null;
  focus: string;
  answer: string | null;
  citations: CitationView[];
  status: "idle" | "answered" | "no_sources";
};

export function initialBriefingState(): BriefingState {
  return {
    error: null,
    documentId: "",
    documentTitle: null,
    focus: "",
    answer: null,
    citations: [],
    status: "idle",
  };
}

export type CompareState = {
  error: string | null;
  documentId: string;
  otherDocumentId: string;
  focus: string;
};

export function initialCompareState(): CompareState {
  return {
    error: null,
    documentId: "",
    otherDocumentId: "",
    focus: "",
  };
}

export type ReviseState = CompareState;

export function initialReviseState(): ReviseState {
  return initialCompareState();
}

export type ReviseVersionState = {
  error: string | null;
};

export function initialReviseVersionState(): ReviseVersionState {
  return { error: null };
}
